// filter choices for one property
// (screen 2 of filter)

import sortBy from 'lodash/sortBy';

import {parseNumber} from 'common/util';
import {useEventListener} from 'common/util/component-helpers';
import {getPropertyValueMappings} from 'common/report/util/mappings';
import {NAVIGATION_KEY_CODES} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {BuilderPaneBase} from 'common/widgets/cohorts-query-builder/common-components/builder-pane-base/index';
import {
  DEFAULT_WINDOW_UNIT,
  FILTER_OPERATORS,
  FILTER_TYPE_LIST,
} from 'common/widgets/cohorts-query-builder/models/constants';
import {
  BooleanOperator,
  DateRangeType,
  DatetimeOperator,
  NumberOperator,
  StringOperator,
  Type,
} from 'common/widgets/cohorts-query-builder/models/enums/builder-enums';
import {
  getIconForPropertyType,
  isDefinedValue,
  removeByValue,
  renameProperty,
  renamePropertyValue,
  sorted,
  truncateMiddle,
} from 'common/widgets/cohorts-query-builder/util/index';
import {isInvalidStagedFilterClause} from 'common/widgets/cohorts-query-builder/util/staged-cohort';
import {TrackingEvent} from 'common/widgets/cohorts-query-builder/util/tracking';

import template from './index.jade';
import css from './index.styl?inline';

const FILTER_DROPDOWNS = {
  Type: `type`,
  Operator: `operator`,
};

const CUSTOM_RANGE_TYPES = {
  [DatetimeOperator.After]: DateRangeType.Since,
  [DatetimeOperator.Between]: DateRangeType.Between,
  [DatetimeOperator.On]: DateRangeType.On,
  [DatetimeOperator.Relative]: DateRangeType.Relative,
  [DatetimeOperator.RelativeFuture]: DateRangeType.RelativeFuture,
  [DatetimeOperator.RelativeBeforeInPast]: DateRangeType.RelativeBeforeInPast,
};

class CohortsBuilderPaneFilterProperty extends BuilderPaneBase {
  shouldUpdate(state) {
    const propertyFilterIdx = state[this.builderStateReference].propertyFilterIdx;
    return !!state.stagedCohort && (isDefinedValue(propertyFilterIdx) || propertyFilterIdx === null);
  }

  connectedCallback() {
    super.connectedCallback();
    useEventListener(this, window, `keydown`, (ev) => {
      if (ev.keyCode === NAVIGATION_KEY_CODES.ENTER) {
        this.updateValueAndSaveStagedFilterClause();
      }
    });
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      helpers: {
        ...super.config.helpers,
        parseNumber,
        getIconForPropertyType,
        getStagedFilterClause: () => this.stagedFilterClause,
        renameProperty: (property) => renameProperty(property, {dataGroupId: this.stagedFilterClause.dataGroupId}),
        truncateMiddle,
        updateRenderedSize: () => this._updateRenderedSize(),
        DATETIME_OPERATOR: DatetimeOperator,
        FILTER_DROPDOWNS,
        FILTER_OPERATORS,
        NUMBER_OPERATOR: NumberOperator,
        STRING_OPERATOR: StringOperator,
        TYPE: Type,
        FILTER_TYPES: FILTER_TYPE_LIST.map((name) => ({
          name,
          icon: getIconForPropertyType(name.toLowerCase()),
        })),
        renamePropertyValue: (value) => renamePropertyValue(value, this.stagedFilterClause.property),
        disableSaveButton: () => {
          const {operator, value, type} = this.stagedFilterClause;
          return isInvalidStagedFilterClause(type, operator, value);
        },

        // General value & type selection
        savePropertyFilter: () => {
          this.updateValueAndSaveStagedFilterClause();
        },
        updateFilterValue: (filterValue) => {
          this.dispatchBubbledEvent(`updated-filter-value`, filterValue);
        },
        updateFilterSearch: (filterSearch) => {
          this.dispatchBubbledEvent(`updated-filter-search`, filterSearch);
        },
        selectFilterOperator: (ev) => {
          this.helpers.closeMenu(FILTER_DROPDOWNS.Operator);
          this.dispatchBubbledEvent(`selected-filter-operator`, ev.detail.value);
        },
        handleFilterOperatorRadioButtonClick: (filterOperator) => {
          this.dispatchBubbledEvent(`selected-filter-operator`, filterOperator);
          if (this.stagedFilterClause.type === Type.Boolean) {
            this.dispatchBubbledEvent(`updated-filter-value`, filterOperator === BooleanOperator.True);
          }
        },
        selectFilterType: (filterType) => {
          this.helpers.closeMenu(FILTER_DROPDOWNS.Type);
          this.dispatchBubbledEvent(`selected-filter-type`, filterType);
        },
        getDateRangePickerAttrs: () => {
          const clause = this.helpers.getStagedFilterClause();
          const clauseValue = clause.value;
          let fromDate, toDate, value, unit;
          if (Array.isArray(clauseValue)) {
            [fromDate, toDate] = clauseValue;
          } else if (clauseValue && clauseValue.window) {
            value = Math.abs(clauseValue.window.value);
            unit = clauseValue.window.unit;
          } else {
            fromDate = clauseValue;
          }

          return {
            'custom-range-type': CUSTOM_RANGE_TYPES[clause.operator],
            from: fromDate,
            preposition: `and`,
            to: toDate,
            unit: unit || DEFAULT_WINDOW_UNIT,
            value: value || ``,
          };
        },
        updateDateFilterValue: (ev) => {
          let filterValue, windowParams;
          switch (this.stagedFilterClause.operator) {
            case DatetimeOperator.Between:
              filterValue = [ev.detail.from, ev.detail.to];
              break;
            case DatetimeOperator.On:
            case DatetimeOperator.After:
              filterValue = ev.detail.from;
              break;
            case DatetimeOperator.RelativeBeforeInPast:
            case DatetimeOperator.Relative:
            case DatetimeOperator.RelativeFuture:
              if (this.stagedFilterClause.operator === DatetimeOperator.RelativeFuture) {
                ev.detail.value = -ev.detail.value;
              }
              windowParams = this.stagedFilterClause.value
                ? this.stagedFilterClause.value.window
                : {unit: DEFAULT_WINDOW_UNIT, value: ``};
              filterValue = {
                window: {unit: ev.detail.unit || windowParams.unit, value: ev.detail.value || windowParams.value},
              };
              break;
            default:
              break;
          }
          this.helpers.updateFilterValue(filterValue);
        },

        // Checkbox helpers (String equals/does not equal)
        handleSelectAllPropertyValues: () => {
          this.helpers.updateFilterValue(this.stagedFilterClause.allValues);
        },
        handleUnselectAllPropertyValues: () => {
          this.helpers.updateFilterValue(null);
        },
        handleTogglePropertyValue: (ev) => {
          const item = ev.detail.item;
          const currentValues = this.stagedFilterClause.value || [];
          const newValues = item.isSelected ? [...currentValues, item.value] : removeByValue(currentValues, item.value);
          this.helpers.updateFilterValue(newValues);
        },
        handleSearchInputInsert: (vnode) => {
          const propertyBuilderSearchInput = vnode.elm.shadowRoot.querySelector(`input`);
          vnode.elm.style.display = `block`;
          if (propertyBuilderSearchInput) {
            propertyBuilderSearchInput.focus();
            this.update({propertyBuilderSearchInput});
          }
        },

        selectAllMatching: ({detail}) => {
          const filterValue = new Set(this.stagedFilterClause.value || []);
          detail.items.forEach((item) => {
            if (item.isSelected) {
              filterValue.add(item.value);
            } else {
              filterValue.delete(item.value);
            }
          });

          this.helpers.updateFilterValue([...filterValue]);

          this.app.track(TrackingEvent.MultipartFilterSelect, {
            '# items': detail.items.length,
            selected: detail.items[0].isSelected,
          });
        },

        // Items menu helpers (String contains/does not contain)
        handleSelectContainsItem: (ev) => {
          this.helpers.updateFilterValue(ev.detail.item.value);
          this.updateValueAndSaveStagedFilterClause();
        },
        handleFilterInputInsert: (vnode) => {
          const propertyBuilderFilterInput = vnode.elm;
          // allow animation of builder screen to finish
          requestAnimationFrame(() => {
            propertyBuilderFilterInput.focus();
            this.update({propertyBuilderFilterInput});
          });
        },

        // Dropdown helpers
        getPropertyValueSections: () => [{items: this.helpers.getPropertyValues()}],
        getPropertyValues: () => {
          const {property, value} = this.stagedFilterClause;
          const allValues = this.stagedFilterClause.allValues || [];
          return sorted(
            allValues.map((v) => ({
              label: renamePropertyValue(v, property),
              value: v,
              isSelected: Array.isArray(value) && value.includes(v),
            })),
            {
              transform: (prop) => {
                const label = prop.label;
                return Number.isNaN(parseInt(label, 10)) ? label.toLowerCase() : parseInt(label);
              },
            },
          );
        },
        isMessagesSentProperty: () => this.stagedFilterClause.property === `$campaigns`,
        getMessagesSentSections: () => {
          const {campaignVariants = []} = getPropertyValueMappings() || {};
          const {value: selectedCampaignId} = this.stagedFilterClause;
          const items = campaignVariants.map((campaign) => ({
            label: campaign.name,
            value: campaign.id,
            isSelected: selectedCampaignId === campaign.id,
          }));
          return [
            {
              items: sortBy(items, [`label`]),
            },
          ];
        },
        isMenuOpen: (menu) => this.getCurrentPaneAttr(`openMenu`) === menu,
        menuChange: (ev, menu) => {
          if (ev.detail && ev.detail.state === `closed`) {
            this.helpers.closeMenu(menu);
          }
        },
        toggleMenu: (menu) => {
          if (!this.helpers.isMenuOpen(menu)) {
            this.updateCurrentBuilderPane({openMenu: menu});
          } else {
            this.helpers.closeMenu(menu);
          }
        },
        closeMenu: (menu) => {
          if (this.helpers.isMenuOpen(menu)) {
            this.updateCurrentBuilderPane({openMenu: null});
          }
        },
      },
    };
  }

  updateValueAndSaveStagedFilterClause() {
    // HACK: datetime inputs require a blur event to update their state and trigger a change event,
    // which then gets picked up by the model. this ensures that happens before we save changes
    this.focus();
    requestAnimationFrame(() => this.dispatchBubbledEvent(`save`, this.stagedFilterClause));
  }

  get property() {
    return this.state.stagedCohort.getProperty(this.state.stagedClausePosition, this.builderState.propertyFilterIdx);
  }

  get stagedFilterClause() {
    return this.state.stagedCohort.getStagedFilterClause(
      this.state.stagedClausePosition,
      this.builderState.propertyFilterIdx,
    );
  }
}

export const CohortsBuilderPaneFilterPropertyEl = defineAndMakeJsxFactory(
  `cohorts-builder-pane-filter-property`,
  CohortsBuilderPaneFilterProperty,
);
